import React, { createRef, useEffect, useState } from 'react';
import {
    Button,
    Link,
    Modal,
    useNavigate,
} from '@alfa-retail-react/components';

import { useApplicationContext } from 'app/store/сontext/ApplicationContext';
import { unauthorizedExceptionHandlerOnSubmitData } from 'app/logic/pages/error/unauthorizedExceptionHandler';
import { networkExceptionHandlerOnSubmitData } from 'app/logic/pages/error/networkExceptionHandler';
import { PageErrors } from 'app/logic/pages/error/PageErrors';
import { exceptionLogger } from 'app/logic/pages/error/exceptionLogger';
import 'app/logic/pages/order/WrongData.less';
import { useIsDesktop } from 'app/utils/useWindowType';
import { CardOrder } from 'app/model/CardOrder';
import { CardOrderCancelReason } from 'app/model/CardOrderCancelReason';
import { ReturnAnalyticsType } from 'app/analytics/useAnalytics';
import { ERROR_PAGE_URL } from 'app/logic/pages/error/ErrorPage';
import { defaultExceptionHandler } from 'app/logic/pages/error/defaultExceptionHandler';
import { useNotificationContext } from 'app/store/сontext/NotificationContextProvider';

export interface WrongDataProps {
    /**
     * Модель кредитной заявки
     */
    order: CardOrder;

    /**
     * Набор инструментов для управления аналитикой
     */
    analytics: ReturnAnalyticsType;

    /**
     * Действие, выполняемое при событии отмены заявки
     * @param orderId идентификатор заявки
     * @param reason причина отклонения заявки
     */
    onCancelOrder: (
        orderId: string,
        reason: CardOrderCancelReason,
    ) => Promise<void>;
}

const WRONG_DATA_ERROR_PAGE_TITLE = 'Заявка отменена';
const WRONG_DATA_ERROR_PAGE_MESSAGE = (
    <span>
        Вы сможете отправить новую заявку на следующий рабочий день после того,
        как обновите паспортные данные в личном кабинете в приложении INSNC.
        Если вы не клиент Альфы, актуализируйте данные в отделении банка, в
        котором обслуживаетесь
    </span>
);

/**
 * Компонент позволяет пользователю сообщить о неверными данными.
 */
export const WrongData = ({
    analytics,
    onCancelOrder,
    order,
}: WrongDataProps) => {
    const [open, setOpen] = React.useState(false);
    const handleModalOpen = () => setOpen(!open);
    const navigate = useNavigate();
    const [isDesktop] = useIsDesktop();
    const pageErrorsComponentRef = createRef<HTMLDivElement>();
    const [pageErrors, setPageErrors] = useState([]);
    const applicationContext = useApplicationContext();
    const notification = useNotificationContext();

    useEffect(() => {
        setPageErrors([]);
    }, [open]);

    const handleResultAction = () => {
        analytics.common('IncorrectActionInitiated');
        onCancelOrder(order.id, CardOrderCancelReason.PERSONAL_DATA_INVALID)
            .then(() => {
                navigate(ERROR_PAGE_URL, {
                    title: WRONG_DATA_ERROR_PAGE_TITLE,
                    messages: [WRONG_DATA_ERROR_PAGE_MESSAGE],
                    actionButtonText: 'Актуализировать в INSNC',
                    actionHandler: () => {
                        analytics.fullEvent(
                            'InsncDataActualizationActionInitiated',
                        );
                        
                        window.open(
                            (window as any).env.MSI_EDIT_PERSONAL_DATA_URL,
                            '_blank',
                        );
                    },
                });
            })
            .catch(exceptionLogger('WrongData', 'onCancelOrder'))
            .catch(
                unauthorizedExceptionHandlerOnSubmitData({
                    navigate,
                    phoneNumber: applicationContext.phoneNumber,
                }),
            )
            .catch(networkExceptionHandlerOnSubmitData({ setPageErrors }))
            .catch(defaultExceptionHandler(notification, analytics));
    };

    return (
        <div className="wrongDataField">
            <Link
                pseudo
                className="wrongDataLink"
                view="default"
                onClick={handleModalOpen}
            >
                У вас другие данные?
            </Link>

            <Modal
                isDesktop={isDesktop}
                className="wrongDataModal"
                title="Актуализируйте паспортные данные"
                open={open}
                onClose={handleModalOpen}
                content={
                    <>
                        Обновите данные в личном кабинете в приложении INSNC.
                        Или обратитесь в отделение банка, в котором
                        обслуживаетесь, если вы не клиент Альфы.
                        {pageErrors.length > 0 ? (
                            <PageErrors
                                className="pageError"
                                ref={pageErrorsComponentRef}
                                errors={pageErrors}
                            />
                        ) : null}
                    </>
                }
                actionButton={
                    <div className={'actionBlock'} ref={pageErrorsComponentRef}>
                        <Button
                            className="confirm"
                            view="accent"
                            size="s"
                            onClick={handleResultAction}
                            block
                        >
                            Отменить заявку
                        </Button>
                        <Button
                            className="confirm"
                            view="secondary"
                            size="s"
                            onClick={handleModalOpen}
                            block
                        >
                            Продолжить оформление
                        </Button>
                    </div>
                }
            />
        </div>
    );
};
